import { useEffect } from "react";

import { isEmpty } from "lodash/fp";
import { useSearchParams } from "react-router-dom";

import Stack from "@mui/material/Stack";

import {
  SectionContainer,
  SectionDivider,
} from "@app/analytics/components/reports/styles";
import EditableReportSubSection from "@app/analytics/MeetingReport/components/EditableReportSubSection";
import EmptyTab from "@app/analytics/MeetingReport/components/EmptyTab";
import { EDITING_TOOLTIP } from "@app/analytics/MeetingReport/constants";
import useMeetingReport from "@app/analytics/MeetingReport/hooks/useMeetingReport";
import useMeetingReportSectionScroll from "@app/analytics/MeetingReport/hooks/useMeetingReportSectionScroll";
import useMeetingReportSessionNotes from "@app/analytics/MeetingReport/hooks/useMeetingReportSessionNotes";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import { useMeetingReportContext } from "@app/analytics/MeetingReport/providers/MeetingReportContext";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import { MainContentContainer } from "@app/analytics/MeetingReport/styles";
import EmptyTranscript from "@app/analytics/MeetingReport/Transcript/EmptyTranscript";
import type { RecapSection } from "@app/analytics/MeetingReport/types";
import { getSummaryCopyContent } from "@app/analytics/MeetingReport/utils/transcriptUtils";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import { elementIsVisible } from "@app/common/hooks/useVisibility";
import { usePageTitle } from "@app/utils/hooks";

import EmptyRecap from "./EmptyRecap";
import MeetingReportRecapCharts from "./MeetingReportRecapCharts";
import MeetingReportRecapNotes from "./MeetingReportRecapNotes";
import MeetingReportRecapSubnav from "./MeetingReportRecapSubnav";
import MeetingReportRecapTranscript from "./MeetingReportRecapTranscript";

const getElementBySearchIndex = (searchIndex?: number) => {
  if (searchIndex == null) return null;
  return document.querySelector(`[data-search-index="${searchIndex}"]`);
};

const MeetingReportRecapContent = ({
  isNotetakerReport = false,
}: {
  isNotetakerReport?: boolean;
}) => {
  usePageTitle("Meeting Report - Recap");
  const [queryParams] = useSearchParams();

  const { canAccessTranscript } = useFeatureAccess();
  const { mainScrollContainer } = useMeetingReportContext();
  const { meetingReport } = useMeetingReport({ enabled: !isNotetakerReport });
  const { isEmpty: transcriptIsEmpty, summary } = useTranscript();

  const {
    data: {
      currentUserCanUpgradeForReport,
      isRestrictedReport,
      shouldUpsellTranscriptEntities,
      tier,
      tierDescription,
      metricsLevel,
      isUploadedSession,
    },
  } = useSessionMetadata();

  const activeSearchResult = useSearchStore(
    (state) => state.searchResults[state.activeSearchResultIndex]
  );

  const inEditMode = useEditStore((state) => state.editingSection != null);

  useEffect(() => {
    if (activeSearchResult?.type !== "note_search_result") {
      // the transcript turns handle scrolling internally, since any DOM
      // searching isn't reliable with the virtualized transcript
      return;
    }

    const resultRef = getElementBySearchIndex(activeSearchResult.index);
    if (!resultRef) return;

    const resultIsVisible = elementIsVisible(
      resultRef as HTMLElement,
      mainScrollContainer.current,
      { topOffset: 100, bottomOffset: 50 }
    );

    if (resultIsVisible) return;

    resultRef.scrollIntoView({
      behavior: "auto",
      block: "center",
      inline: "center",
    });
  }, [activeSearchResult]);

  const preselectedSection = queryParams.get("section") as RecapSection;

  // show the charts unless there is nothing to show
  const showCharts =
    !isUploadedSession && // uploaded sessions do not have affective metrics
    meetingReport?.engagement != null &&
    metricsLevel !== "objective_metrics";

  const { notes: sessionNotes, isInitialLoading: isNotesInitialLoading } =
    useMeetingReportSessionNotes({
      enabled: !isRestrictedReport,
    });

  const [selectedSection, setSelectedSection] =
    useMeetingReportSectionScroll<RecapSection>({
      defaultSection: "notes",
      preselectedSection,
      enabled: !transcriptIsEmpty,
      sections: ["notes", "transcript"],
    });

  const chartProps = {
    tier,
    tierDescription,
    meetingScore: {
      overall: Math.round(meetingReport?.callScore ?? 0),
      trend: meetingReport?.callScoreTrend ?? [],
    },
    engagement: {
      overall: Math.round(meetingReport?.engagement ?? 0),
      trend: meetingReport?.engagementTrend ?? [],
    },
    sentiment: {
      overall: Math.round(meetingReport?.sentiment ?? 0),
      trend: meetingReport?.sentimentTrend ?? [],
    },
  };

  const hasNoContent =
    transcriptIsEmpty &&
    !isNotesInitialLoading &&
    isEmpty(sessionNotes) &&
    !isNotetakerReport;

  if (isRestrictedReport && !currentUserCanUpgradeForReport) {
    return (
      <EmptyTab
        tab="Recap"
        title="Get unlimited reports"
        canUpgradeCurrentReport={currentUserCanUpgradeForReport}
      />
    );
  }

  if (hasNoContent && !shouldUpsellTranscriptEntities) {
    return (
      <>
        {showCharts && (
          <>
            <SectionContainer>
              <Stack paddingTop={2} px={2}>
                <MeetingReportRecapCharts {...chartProps} />
              </Stack>
            </SectionContainer>
            <SectionDivider />
          </>
        )}
        <SectionContainer sx={{ py: 2, display: "flex", flex: 1 }}>
          <EmptyRecap />
        </SectionContainer>
      </>
    );
  }

  return (
    <>
      <MeetingReportRecapSubnav
        copyContent={getSummaryCopyContent(
          summary,
          sessionNotes,
          isNotetakerReport
        )}
        isNotetakerReport={isNotetakerReport}
        isRestrictedReport={isRestrictedReport}
        onSectionSelect={setSelectedSection}
        selectedSection={selectedSection}
      />
      <MainContentContainer>
        <EditableReportSubSection
          id="notes"
          disabledTooltip={EDITING_TOOLTIP.editingTranscript}
        >
          {showCharts && <MeetingReportRecapCharts {...chartProps} />}
          <MeetingReportRecapNotes
            isNotetakerReport={isNotetakerReport}
            isRestrictedReport={isRestrictedReport}
            shouldUpsell={shouldUpsellTranscriptEntities}
          />
        </EditableReportSubSection>
        {!isNotetakerReport && (
          <>
            {/* Hide the divider in edit mode since it causes an
            awkward 1px gap between overlays */}
            {!inEditMode && <SectionDivider />}
            <EditableReportSubSection
              id="transcript"
              disabledTooltip={EDITING_TOOLTIP.editingNotes}
              sx={{ pt: 2 }}
            >
              {canAccessTranscript ? (
                <MeetingReportRecapTranscript />
              ) : (
                <EmptyTranscript type="unauthorized" />
              )}
            </EditableReportSubSection>
          </>
        )}
      </MainContentContainer>
    </>
  );
};

export default MeetingReportRecapContent;
